import UnavailablePromotionProductsTable from 'containers/Tables/UnavailablePromotionProductsTable';
import { Summary } from 'contexts/Promotion/interfaces';
import { FindBasketProductsResponse } from 'services/basket/interfaces';
import { GetPromotionInfoResponse, ProductInComboProps } from 'services/products/interfaces';
import { GetDialogProps, HasUnableToUpdateParams, ReorderItemsParams } from './interfaces';
import { DialogState } from 'containers/Drawers/ImportPromotionDetailDrawerContent/interfaces';
import { ShowUnavailableProductsDialogParams } from 'pages/products/PromotionDetails/hooks/usePromotionDetailsRules/interfaces';

export const getSummaryQuantities = (summary: Summary[]) => summary.reduce((acc, curr) => curr.quantity + acc, 0);

export const getSummaryBrutePrice = (summary: Summary[]) =>
	summary.reduce((acc, curr) => curr.quantity * curr.price + acc, 0);

export const getCurrentComponentRange = (
	rangeByQuantity: boolean,
	progressiveDiscount: boolean,
	summary: Summary[],
	currentQuantityInBasket = 1,
) => {
	if (rangeByQuantity)
		return progressiveDiscount ? currentQuantityInBasket : getSummaryQuantities(summary) * currentQuantityInBasket;
	return getSummaryBrutePrice(summary) * currentQuantityInBasket;
};

export const handlerDistributorValidity = (data: GetPromotionInfoResponse) =>
	!!data?.distributors?.length && data?.distributors.some(({ status }) => status !== 'UNAVAILABLE');

export const hasQuantityChanged = (
	currentData: Summary[],
	promotionId: string,
	initialData?: FindBasketProductsResponse,
) => {
	const productsInCombo = initialData?.baskets
		?.flatMap(({ products }) => products)
		.find(({ id }) => id === promotionId)?.productsInCombo;

	const hasAnyChangesInQuantity =
		productsInCombo?.map(
			({ quantity, productId }) =>
				quantity === currentData.find((product) => product.productId === productId)?.quantity,
		) || [];

	if (currentData?.length !== hasAnyChangesInQuantity.length) {
		const productsWithQuantitiesZero =
			currentData?.reduce<boolean[]>((prev, { productId, quantity }) => {
				if (!productsInCombo?.find((p) => p.productId === productId)) {
					prev.push(quantity === 0);
				}
				return prev;
			}, []) || [];

		const hasAnyChanges = [...productsWithQuantitiesZero, ...(hasAnyChangesInQuantity || [])];
		return !hasAnyChanges.every(Boolean);
	}

	return !hasAnyChangesInQuantity.every(Boolean);
};

export const getDiscountPrice = (discount: number, price: number) => (discount / 100 - 1) * -1 * price;

export const hasUnableToUpdate = ({
	addedProductsInComboUnits: addedProductsInCombo,
	currentQuantityInBasket,
	hasQuantityChanged: quantityChanged,
	hasValidDistributors,
	minQuantityDistinctProducts,
}: HasUnableToUpdateParams) => {
	if (minQuantityDistinctProducts) {
		if (!currentQuantityInBasket || quantityChanged) {
			return addedProductsInCombo < minQuantityDistinctProducts;
		}

		return true;
	}

	if (!hasValidDistributors) {
		return true;
	}

	if (currentQuantityInBasket) {
		if (quantityChanged) return false;
		return true;
	}

	return false;
};

export const getUnavailableProductsDialogContent = ({
	distributor,
	productsUnavailable,
	isAllUpdatedProductsUnavailable,
}: ShowUnavailableProductsDialogParams) => {
	const dialogDescription = isAllUpdatedProductsUnavailable
		? 'Todos os produtos escolhidos não estão disponíveis no distribuidor'
		: 'Os produtos listados abaixo não estão disponíveis no distribuidor';

	return {
		description: (
			<>
				{dialogDescription} <strong>{distributor.distributorName}</strong>, caso deseje prosseguir, os itens serão{' '}
				<strong>removidos</strong> do seu carrinho.
			</>
		),
		children: isAllUpdatedProductsUnavailable ? undefined : (
			<UnavailablePromotionProductsTable
				data={productsUnavailable.map(({ description, ean13, quantity }) => ({ description, ean13, quantity }))}
			/>
		),
	};
};

export const getDialogProps = (state: DialogState, action?: GetDialogProps) => {
	if (state === 'conflict')
		return {
			heading: { title: 'Conflito de produtos', showCloseButton: true },
			content: {
				description:
					'Já existe esse produto em seu carrinho. Para comprá-lo com outra condição comercial, você deve removê-lo do carrinho.',
			},
			footer: {
				primaryButton: action?.handleOpenSideBasket
					? { label: 'Ver carrinho', onClick: action?.handleOpenSideBasket }
					: undefined,
				secondaryButton: { label: 'Cancelar' },
			},
		};

	if (state === 'distributor')
		return {
			heading: {
				title: 'Deseja continuar?',
				showCloseButton: true,
			},
			content: {
				description:
					'Atenção! Caso tenha algum pedido em seu carrinho do(s) distribuidor(es) alterado(s), o mesmo será excluído. Deseja continuar?',
			},
			onClose: action?.handleDialogClose,
			footer: {
				primaryButton: { label: 'Continuar', onClick: action?.handleRemovePromotion },
				secondaryButton: { label: 'Ver carrinho', onClick: action?.handleOpenSideBasket },
			},
		};

	if (state === 'no_value')
		return {
			heading: {
				title: 'Combo sem quantidade',
				showCloseButton: true,
			},
			content: {
				description: 'Adicione a quantidade dos produtos desejada para continuar',
			},
			footer: {
				primaryButton: { label: 'Entendi' },
			},
		};

	return {
		heading: {
			title: 'Atualizar carrinho',
			showCloseButton: true,
		},
		content: {
			description: 'Foram adicionados mais itens ao seu combo, para prosseguir você deve atualizar o seu carrinho.',
		},
		footer: {
			primaryButton: { label: 'Voltar a promoção' },
		},
	};
};

const getPrice = (product: ProductInComboProps, selectedDistributor: string, sortSelected: number) => {
	const valueFallback = sortSelected === 1 ? -Infinity : Infinity;
	const distributor = product.distributors.find((d) => d.distributorId === selectedDistributor && d.valid);
	return distributor?.price ?? valueFallback;
};

const getDiscountedPrice = ({
	basePrice,
	product,
	quantity,
	rangeByQuantity,
}: {
	product: ProductInComboProps;
	quantity: number;
	basePrice: number;
	rangeByQuantity?: boolean;
}) => {
	const quantityFallback = quantity || 1;
	const comparator = rangeByQuantity ? quantity : basePrice * quantity;

	const discount = product.ranges?.find(
		(range) => comparator >= range.from && comparator <= (range.to ?? 99999),
	)?.discount;

	return (discount ? basePrice - basePrice * (discount / 100) : basePrice) * quantityFallback;
};

const sortByPrice = (
	products: ProductInComboProps[],
	selectedDistributor: string,
	summary: Summary[] | undefined,
	rangeByQuantity: boolean | undefined,
	isAscending: boolean,
	sortSelected: number,
) => {
	return products
		.filter((product) => product.distributors.some((d) => d.distributorId === selectedDistributor && d.price !== null))
		.sort((productA, productB) => {
			const priceA = getPrice(productA, selectedDistributor, sortSelected);
			const priceB = getPrice(productB, selectedDistributor, sortSelected);

			const productAQuantity = summary?.find((item) => item.productId === productA.productId)?.quantity || 0;
			const productBQuantity = summary?.find((item) => item.productId === productB.productId)?.quantity || 0;

			const finalPriceA = getDiscountedPrice({
				product: productA,
				basePrice: priceA,
				quantity: productAQuantity,
				rangeByQuantity,
			});
			const finalPriceB = getDiscountedPrice({
				product: productB,
				basePrice: priceB,
				quantity: productBQuantity,
				rangeByQuantity,
			});

			return isAscending ? finalPriceA - finalPriceB : finalPriceB - finalPriceA;
		});
};

const sortAlphabetically = (products: ProductInComboProps[]) => {
	return [...products].sort((productA, productB) => {
		const nameA = productA.productInfo?.description?.toLowerCase() || '';
		const nameB = productB.productInfo?.description?.toLowerCase() || '';

		return nameA.localeCompare(nameB, 'pt-BR', {
			sensitivity: 'base',
			numeric: true,
		});
	});
};

const sortBySelectedProducts = (products: ProductInComboProps[], selectedProducts: string[] | undefined) => {
	return selectedProducts?.length
		? [...products].sort((a, b) => {
				const aIndex = selectedProducts.indexOf(a.productId);
				const bIndex = selectedProducts.indexOf(b.productId);

				const bothNotSelected = aIndex === -1 && bIndex === -1;
				const aNotSelected = aIndex === -1;
				const bNotSelected = bIndex === -1;

				return bothNotSelected ? 0 : aNotSelected ? 1 : bNotSelected ? -1 : aIndex - bIndex;
		  })
		: products;
};

export const reorderItems = ({
	data,
	selectedDistributor,
	selectedProducts,
	sortSelected,
	rangeByQuantity,
	summary,
}: ReorderItemsParams) => {
	const originalData = [...(data || [])];

	const sortStrategies = {
		0: () => originalData,
		1: () => sortByPrice(originalData, selectedDistributor, summary, rangeByQuantity, false, sortSelected || 1),
		2: () => sortByPrice(originalData, selectedDistributor, summary, rangeByQuantity, true, sortSelected || 2),
		3: () => sortAlphabetically(originalData),
		4: () => sortBySelectedProducts(originalData, selectedProducts),
	} as const;

	const sortFunction = sortStrategies[sortSelected as keyof typeof sortStrategies];

	return sortFunction?.() ?? originalData;
};
